import React, { useState, useEffect, useCallback, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAuth0 } from '@auth0/auth0-react';
import { DesiredAction, DesiredActionKey } from '@dreamai/types';
import api from '../../../api';
import { getActionKeyLabel } from '../../../lib/desiredActionFields';
import { useProjectAppLoading } from '../../../providers/ProjectAppViewLoadingProvider';
import { MapLinkProvider } from './MapLinkContext';
import ProjectAppTableBlock from './ProjectAppTableBlock';
import ProjectAppMapBlock from './ProjectAppMapBlock';
import ProjectAppDataTableBlock from './ProjectAppDataTableBlock';
import ProjectAppKanbanBlock from './ProjectAppKanbanBlock';
import ProjectAppTimelineBlock from './ProjectAppTimelineBlock';
import ProjectAppStackedDataBlock from './ProjectAppStackedDataBlock';
import ProjectAppItineraryBlock from './ProjectAppItineraryBlock';
import ProjectAppEmailContentBlock from './ProjectAppEmailContentBlock';
import ResearchSuggestions, { ACTION_ICONS } from './ResearchSuggestions';
import './project-app-blocks.scss';

/** Ensure single \n renders as a hard line break in Markdown (two trailing spaces). */
const mdBreaks = (text: string) => text.replace(/\n/g, '  \n');

export interface Block {
  id: string;
  type: string;
  row: number;
  column: number;
  rowSpan: number;
  colSpan: number;
  data: Record<string, unknown>;
}

/** Identifies a completed desired action for UI animation */
export interface CompletedAction {
  /** 'block:${blockId}:${actionKey}' or 'suggestion:${actionKey}' */
  id: string;
  /** 'created' = show tick animation, 'view' = show View in Todo List */
  phase: 'created' | 'view';
}

interface ProjectAppBlockRendererProps {
  appId: string;
  desiredActions?: DesiredAction[];
  onSuggestionClick?: (action: DesiredAction) => void;
  onCardActionClick?: (action: DesiredAction, blockId: string) => void;
  agentName?: string;
  completedActions?: CompletedAction[];
}

/**
 * Fetches and renders project app blocks in a single 6-column CSS grid.
 * Each block declares its own row, column, rowSpan, and colSpan.
 * Automatically re-fetches when the loading provider signals a PVA run has finished.
 */
const ProjectAppBlockRenderer: React.FC<ProjectAppBlockRendererProps> = ({
  appId,
  desiredActions,
  onSuggestionClick,
  onCardActionClick,
  agentName,
  completedActions,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const { finishedKey } = useProjectAppLoading();
  const [blocks, setBlocks] = useState<Block[]>([]);
  const [threadId, setThreadId] = useState<string | null>(null);
  const [appName, setAppName] = useState<string | null>(null);
  const [heroImage, setHeroImage] = useState<string | null>(null);
  const [heroImageCaption, setHeroImageCaption] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // NOTE: we intentionally do NOT push this hero image into
  // HeroImageContext / ChatTopBanner. Doing so caused the chat canvas
  // header bar to get replaced with a blurred version of the research
  // page's hero image every time you opened one, which was extremely
  // jarring. The hero image is rendered inline in the research page
  // content below; the canvas header stays untouched.

  const fetchBlocks = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const response = await api.get(`/project-apps/${appId}/blocks`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data?.success) {
        setBlocks(response.data.blocks);
        if (response.data.threadId) {
          setThreadId(response.data.threadId);
        }
        if (response.data.appName) {
          setAppName(response.data.appName);
        }
        setHeroImage(response.data.heroImage || null);
        setHeroImageCaption(response.data.heroImageCaption || null);
      }
    } catch (err) {
      console.error('Error fetching project app blocks:', err);
    } finally {
      setLoading(false);
    }
  }, [appId, getAccessTokenSilently]);

  // Fetch on mount and whenever finishedKey changes (PVA run completed)
  useEffect(() => {
    fetchBlocks();
  }, [fetchBlocks, finishedKey]);

  if (loading && blocks.length === 0) {
    return (
      <div className='project-app-blocks-loading'>
        <div className='loading-spinner' />
      </div>
    );
  }

  // Compute how many rows the grid needs
  const maxRow =
    blocks.length > 0
      ? blocks.reduce((max, b) => Math.max(max, b.row + b.rowSpan), 0)
      : 0;

  return (
    <MapLinkProvider>
      <div className='project-app-page'>
        {heroImage ? (
          <div className='project-app-hero-header'>
            {appName && <h1 className='project-app-hero-title'>{appName}</h1>}
            {desiredActions && desiredActions.length > 0 && (
              <ResearchSuggestions
                desiredActions={desiredActions}
                onSuggestionClick={onSuggestionClick}
                agentName={agentName}
                completedActions={completedActions}
              />
            )}
            <div className='project-app-hero-divider' />
          </div>
        ) : (
          desiredActions &&
          desiredActions.length > 0 && (
            <ResearchSuggestions
              desiredActions={desiredActions}
              onSuggestionClick={onSuggestionClick}
              agentName={agentName}
              completedActions={completedActions}
            />
          )
        )}
        {blocks.length > 0 && (
          <div
            className='project-app-blocks'
            style={{
              gridTemplateRows: `repeat(${maxRow}, auto)`,
            }}
          >
            {(() => {
              // Build a count of blocks per row to detect lone blocks
              const blocksPerRow = new Map<number, number>();
              for (const b of blocks) {
                blocksPerRow.set(b.row, (blocksPerRow.get(b.row) || 0) + 1);
              }

              // ── Collision fix: detect overlapping blocks on the same row ──
              // Sort blocks by row then column, then check for overlaps.
              // When two blocks on the same row share column space, push the
              // later one to a new row so they don't render on top of each other.
              const rowOffsets = new Map<string, number>(); // blockId → extra row offset
              const rowGroups = new Map<number, typeof blocks>();
              for (const b of blocks) {
                if (!rowGroups.has(b.row)) rowGroups.set(b.row, []);
                rowGroups.get(b.row)!.push(b);
              }
              let extraRowShift = 0;
              const sortedOriginalRows = Array.from(rowGroups.keys()).sort(
                (a, b) => a - b,
              );
              for (const row of sortedOriginalRows) {
                const group = rowGroups.get(row)!;
                if (group.length <= 1) {
                  for (const b of group) rowOffsets.set(b.id, extraRowShift);
                  continue;
                }
                // Sort by column, then by colSpan ascending (narrower first)
                group.sort(
                  (a, b) => a.column - b.column || a.colSpan - b.colSpan,
                );
                // Greedily assign: first block stays, any overlapping block goes to a new row
                let occupiedEnd = group[0].column + group[0].colSpan;
                rowOffsets.set(group[0].id, extraRowShift);
                for (let i = 1; i < group.length; i++) {
                  const b = group[i];
                  if (b.column < occupiedEnd) {
                    // Overlap — bump to next row
                    extraRowShift++;
                    rowOffsets.set(b.id, extraRowShift);
                    // Re-count this bumped block for blocksPerRow
                    blocksPerRow.set(b.row, (blocksPerRow.get(b.row) || 1) - 1);
                  } else {
                    occupiedEnd = b.column + b.colSpan;
                    rowOffsets.set(b.id, extraRowShift);
                  }
                }
              }

              // Row-level analysis for spacing and card-pair detection
              const CONTENT_BLOCK_TYPES = new Set([
                'markdown',
                'table',
                'data_table',
                'map',
                'image',
                'kanban',
                'timeline',
                'stacked_data',
                'itinerary',
                'email_content',
              ]);
              const rowMeta = new Map<
                number,
                { types: Set<string>; allVerticalCards: boolean }
              >();
              for (const b of blocks) {
                if (!rowMeta.has(b.row)) {
                  rowMeta.set(b.row, {
                    types: new Set(),
                    allVerticalCards: true,
                  });
                }
                const info = rowMeta.get(b.row)!;
                info.types.add(b.type);
                if (
                  b.type !== 'card' ||
                  (b.data as Record<string, unknown>)?.layout === 'horizontal'
                ) {
                  info.allVerticalCards = false;
                }
              }
              const sortedRows = Array.from(rowMeta.keys()).sort(
                (a, b) => a - b,
              );

              return blocks.map((block) => {
                const extraClass =
                  block.type === 'heading'
                    ? ` project-app-block--heading-h${(block.data as any)?.level || 1}`
                    : '';
                // Vertical cards: enforce min colSpan=3 (max 2 per row)
                let effectiveColSpan = block.colSpan;
                if (
                  block.type === 'card' &&
                  (block.data as any)?.layout !== 'horizontal' &&
                  effectiveColSpan < 3
                ) {
                  effectiveColSpan = 3;
                }

                // Auto-expand: if this block is alone on its row, stretch to full width
                const isAloneOnRow = blocksPerRow.get(block.row) === 1;
                let effectiveColumn = block.column;
                if (isAloneOnRow && effectiveColSpan < 6) {
                  effectiveColSpan = 6;
                  effectiveColumn = 0;
                }

                // Spacing: content block not preceded by a heading row
                const rowIdx = sortedRows.indexOf(block.row);
                const prevRowMeta =
                  rowIdx > 0 ? rowMeta.get(sortedRows[rowIdx - 1]) : null;
                const needsContentGap =
                  CONTENT_BLOCK_TYPES.has(block.type) &&
                  rowIdx > 0 &&
                  !prevRowMeta?.types.has('heading');

                // Card pair: vertical card on a row of only vertical cards
                const isVerticalCard =
                  block.type === 'card' &&
                  (block.data as Record<string, unknown>)?.layout !==
                    'horizontal';
                const ri = rowMeta.get(block.row);
                const isCardPairRow =
                  isVerticalCard &&
                  ri?.allVerticalCards &&
                  (blocksPerRow.get(block.row) || 0) > 1;

                return (
                  <div
                    key={block.id}
                    className={[
                      'project-app-block',
                      `project-app-block--${block.type}`,
                      extraClass.trim(),
                      needsContentGap ? 'content-gap' : '',
                      isCardPairRow ? 'card-pair-row' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    style={
                      {
                        gridRow: `${block.row + 1 + (rowOffsets.get(block.id) || 0)} / span ${block.rowSpan}`,
                        gridColumn: `${effectiveColumn + 1} / span ${effectiveColSpan}`,
                        '--col-span': effectiveColSpan,
                        '--col-span-half': Math.ceil(effectiveColSpan / 2),
                      } as React.CSSProperties
                    }
                  >
                    <BlockContent
                      block={block}
                      threadId={threadId}
                      onCardActionClick={onCardActionClick}
                      completedActions={completedActions}
                      agentName={agentName}
                    />
                  </div>
                );
              });
            })()}
          </div>
        )}
      </div>
    </MapLinkProvider>
  );
};

/**
 * Renders the content of a single block based on its type.
 */
const PASTEL_COLORS: Record<string, string> = {
  gray: '#F3F3F5',
  yellow: '#FFF7CC',
  green: '#D2F9F4',
  blue: '#D5E9F6',
  white: '#FFFFFF',
  red: '#FBD0D9',
};

const PASTEL_TEXT_COLORS: Record<string, string> = {
  gray: '#838B9B',
  red: '#5E081A',
  green: '#0C5A50',
  blue: '#255B8F',
  yellow: '#665600',
  white: '#000D26',
};

export const BlockContent: React.FC<{
  block: Block;
  threadId: string | null;
  onCardActionClick?: (action: DesiredAction, blockId: string) => void;
  completedActions?: CompletedAction[];
  agentName?: string;
}> = ({
  block,
  threadId,
  onCardActionClick,
  completedActions = [],
  agentName,
}) => {
  switch (block.type) {
    case 'heading': {
      const { text, level, color } = block.data as {
        text?: string;
        level?: 1 | 2 | 3;
        color?: string;
      };
      const headingLevel = level || 1;
      const style: React.CSSProperties | undefined =
        color && PASTEL_COLORS[color]
          ? {
              backgroundColor: PASTEL_COLORS[color],
              color: PASTEL_TEXT_COLORS[color] || undefined,
              padding: '4px 8px',
              borderRadius: '4px',
              display: 'inline-block',
            }
          : undefined;
      const className = `project-app-block-heading project-app-block-heading--h${headingLevel}`;
      return React.createElement(
        `h${headingLevel}`,
        { className, style },
        text || '',
      );
    }
    case 'markdown': {
      const { content } = block.data as { content?: string };
      return (
        <div className='project-app-block-markdown'>
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {mdBreaks(content || '')}
          </ReactMarkdown>
        </div>
      );
    }
    case 'table':
      return <ProjectAppTableBlock data={block.data} />;
    case 'map':
      return <ProjectAppMapBlock data={block.data} />;
    case 'data_table':
      return <ProjectAppDataTableBlock blockId={block.id} data={block.data} />;
    case 'kanban':
      return <ProjectAppKanbanBlock blockId={block.id} data={block.data} />;
    case 'timeline':
      return <ProjectAppTimelineBlock data={block.data} />;
    case 'stacked_data':
      return (
        <ProjectAppStackedDataBlock blockId={block.id} data={block.data} />
      );
    case 'itinerary':
      return <ProjectAppItineraryBlock blockId={block.id} data={block.data} />;
    case 'email_content':
      return (
        <ProjectAppEmailContentBlock
          blockId={block.id}
          data={block.data}
          onCardActionClick={onCardActionClick}
          completedActions={completedActions}
          agentName={agentName}
        />
      );
    case 'image': {
      const { src, alt, caption } = block.data as {
        src?: string;
        alt?: string;
        caption?: string;
      };
      return (
        <div className='project-app-block-image'>
          {src ? (
            <img src={src} alt={alt || ''} />
          ) : (
            <div className='project-app-block-image__placeholder'>No image</div>
          )}
          {caption && (
            <p className='project-app-block-image__caption'>{caption}</p>
          )}
        </div>
      );
    }
    case 'number': {
      const { value, label, color } = block.data as {
        value?: number | string;
        label?: string;
        color?: string;
      };
      const bgColor =
        color && PASTEL_COLORS[color] ? PASTEL_COLORS[color] : undefined;
      const textColor =
        color && PASTEL_TEXT_COLORS[color]
          ? PASTEL_TEXT_COLORS[color]
          : undefined;
      return (
        <div
          className='project-app-block-number'
          style={
            bgColor ? { backgroundColor: bgColor, color: textColor } : undefined
          }
        >
          <span className='project-app-block-number__value'>
            {value != null ? value : '—'}
          </span>
          {label && (
            <span className='project-app-block-number__label'>{label}</span>
          )}
        </div>
      );
    }
    case 'card': {
      const {
        image,
        heading,
        text,
        subText,
        links,
        desiredActions: cardActions,
        farRightText,
        layout,
      } = block.data as {
        image?: string;
        heading?: string;
        text?: string;
        subText?: string;
        links?: { label: string; url: string }[];
        desiredActions?: DesiredAction[];
        farRightText?: string;
        layout?: 'vertical' | 'horizontal';
      };
      const cardClass = `project-app-block-card${layout === 'horizontal' ? ' project-app-block-card--horizontal' : ''}`;
      const actionsEl =
        cardActions && cardActions.length > 0 ? (
          <div className='project-app-block-card__actions'>
            {cardActions.map((action, i) => {
              const Icon = ACTION_ICONS[action.key];
              const actionLabel = getActionKeyLabel(action.key, agentName);
              const completed = completedActions.find(
                (c) => c.id === `block:${block.id}:${action.key}`,
              );
              if (completed) {
                return (
                  <div
                    key={`${action.key}-${i}`}
                    className={`project-app-block-card__action-btn completed ${completed.phase}`}
                  >
                    {completed.phase === 'created' ? (
                      <span className='project-app-block-card__action-tick'>
                        ✓ Todo Created
                      </span>
                    ) : (
                      <a
                        className='project-app-block-card__action-view-link'
                        href='/'
                        onClick={(e) => {
                          e.preventDefault();
                          window.location.href = '/';
                        }}
                      >
                        View in Todo List →
                      </a>
                    )}
                  </div>
                );
              }
              return (
                <button
                  key={`${action.key}-${i}`}
                  className='project-app-block-card__action-btn'
                  onClick={() => onCardActionClick?.(action, block.id)}
                >
                  {Icon && (
                    <Icon className='project-app-block-card__action-icon' />
                  )}
                  <div className='project-app-block-card__action-text'>
                    <span className='project-app-block-card__action-key'>
                      {actionLabel}
                    </span>
                    {action.label && (
                      <span className='project-app-block-card__action-label'>
                        {action.label}
                      </span>
                    )}
                  </div>
                  <span className='project-app-block-card__action-arrow'>
                    →
                  </span>
                </button>
              );
            })}
          </div>
        ) : null;
      return (
        <div className={cardClass}>
          {image && (
            <div className='project-app-block-card__image'>
              <img
                src={image}
                alt={heading || ''}
                onLoad={(e) => {
                  const img = e.currentTarget;
                  if (img.naturalHeight > img.naturalWidth) {
                    img.classList.add('portrait');
                  }
                }}
              />
            </div>
          )}
          <div className='project-app-block-card__body'>
            <div className='project-app-block-card__main'>
              {heading && (
                <h3 className='project-app-block-card__heading'>{heading}</h3>
              )}
              {text && (
                <div className='project-app-block-card__text'>
                  <ReactMarkdown>{mdBreaks(text)}</ReactMarkdown>
                </div>
              )}
              {subText && (
                <div className='project-app-block-card__subtext'>
                  <ReactMarkdown>{mdBreaks(subText)}</ReactMarkdown>
                </div>
              )}
              {links && links.length > 0 && (
                <div className='project-app-block-card__links'>
                  {links.map((link, i) => (
                    <a
                      key={i}
                      href={link.url}
                      target='_blank'
                      rel='noopener noreferrer'
                    >
                      {link.label}
                    </a>
                  ))}
                </div>
              )}
            </div>
            {farRightText && (
              <span className='project-app-block-card__far-right'>
                {farRightText}
              </span>
            )}
            {layout === 'horizontal' && actionsEl}
          </div>
          {layout !== 'horizontal' && actionsEl}
        </div>
      );
    }
    default:
      return (
        <div className='project-app-block-unknown'>
          Unknown block type: {block.type}
        </div>
      );
  }
};

export default ProjectAppBlockRenderer;
